import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  Gem, 
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  Zap,
  ArrowRight,
  Crown,
  CircleDot,
  User,
  UserCircle,
  Gift,
  Calendar,
  Palette,
  Ruler
} from 'lucide-react';
import { toast } from 'sonner';

// Jewelry types
const JEWELRY_TYPES = [
  { value: 'anello', label: 'Anello', icon: CircleDot },
  { value: 'collana', label: 'Collana / Ciondolo', icon: Gem },
  { value: 'bracciale', label: 'Bracciale', icon: Sparkles },
  { value: 'orecchini', label: 'Orecchini', icon: Gem },
  { value: 'fede', label: 'Fede nuziale', icon: Heart },
  { value: 'anello_fidanzamento', label: 'Anello di fidanzamento', icon: Gem },
  { value: 'parure', label: 'Parure / Set completo', icon: Crown },
  { value: 'spilla', label: 'Spilla / Brooch', icon: Sparkles },
  { value: 'gemelli', label: 'Gemelli da polso', icon: Gem },
  { value: 'tiara', label: 'Tiara / Diadema', icon: Crown },
  { value: 'altro', label: 'Altro', icon: Gem },
];

// Material preferences
const MATERIALS = [
  { value: 'oro_giallo', label: 'Oro giallo', icon: Gem },
  { value: 'oro_bianco', label: 'Oro bianco', icon: Gem },
  { value: 'oro_rosa', label: 'Oro rosa', icon: Gem },
  { value: 'argento', label: 'Argento 925', icon: Gem },
  { value: 'platino', label: 'Platino', icon: Gem },
  { value: 'vermeil', label: 'Vermeil (argento dorato)', icon: Gem },
  { value: 'ottone', label: 'Ottone / Bronzo', icon: Gem },
  { value: 'acciaio', label: 'Acciaio inossidabile', icon: Gem },
  { value: 'misto', label: 'Materiali misti', icon: Palette },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Sparkles },
];

// Occasion/Purpose
const JEWELRY_OCCASIONS = [
  { value: 'fidanzamento', label: 'Fidanzamento', icon: Heart },
  { value: 'matrimonio', label: 'Matrimonio', icon: Heart },
  { value: 'anniversario', label: 'Anniversario', icon: Calendar },
  { value: 'compleanno', label: 'Compleanno', icon: Gift },
  { value: 'laurea', label: 'Laurea', icon: Star },
  { value: 'nascita', label: 'Nascita / Battesimo', icon: Heart },
  { value: 'regalo', label: 'Regalo speciale', icon: Gift },
  { value: 'uso_quotidiano', label: 'Uso quotidiano', icon: Sparkles },
  { value: 'evento_speciale', label: 'Evento / Cerimonia', icon: Crown },
  { value: 'collezione', label: 'Per la mia collezione', icon: Gem },
  { value: 'altro', label: 'Altro', icon: Gem },
];

// Recipient options
const JEWELRY_RECIPIENTS = [
  { value: 'me_stessa', label: 'Per me stessa', icon: Sparkles },
  { value: 'me_stesso', label: 'Per me stesso', icon: Sparkles },
  { value: 'partner_donna', label: 'Per la mia partner', icon: Heart },
  { value: 'partner_uomo', label: 'Per il mio partner', icon: Heart },
  { value: 'mamma', label: 'Per mia mamma', icon: Heart },
  { value: 'figlia', label: 'Per mia figlia', icon: Heart },
  { value: 'amica', label: 'Per un\'amica', icon: UserCircle },
  { value: 'amico', label: 'Per un amico', icon: User },
  { value: 'neonato', label: 'Per un neonato', icon: Heart },
  { value: 'coppia', label: 'Per una coppia', icon: Users },
  { value: 'altro', label: 'Altro', icon: Gift },
];

// Budget options
const BUDGET_OPTIONS = [
  { value: '50-100', label: '€50 - €100', description: 'Bijoux e argento' },
  { value: '100-250', label: '€100 - €250', description: 'Argento pregiato' },
  { value: '250-500', label: '€250 - €500', description: 'Oro e pietre' },
  { value: '500-1000', label: '€500 - €1.000', description: 'Gioiello importante' },
  { value: '1000-2000', label: '€1.000 - €2.000', description: 'Alta gioielleria' },
  { value: '2000-5000', label: '€2.000 - €5.000', description: 'Pezzo unico pregiato' },
  { value: '5000-10000', label: '€5.000 - €10.000', description: 'Gioiello di lusso' },
  { value: '10000+', label: '€10.000+', description: 'Haute joaillerie' },
];

// Timeline options
const TIMELINE_OPTIONS = [
  { value: '2weeks', label: '2 Settimane', description: 'Urgente' },
  { value: '1month', label: '1 Mese', description: 'Prioritario' },
  { value: '2months', label: '2 Mesi', description: 'Standard' },
  { value: '3months', label: '3 Mesi', description: 'Con calma' },
  { value: '6months', label: '6 Mesi', description: 'Progetto speciale' },
  { value: 'flexible', label: 'Flessibile', description: 'Nessuna urgenza' },
];

// Example jewelry
const JEWELRY_EXAMPLES = [
  {
    idea: "Anello fidanzamento con zaffiro blu",
    result: "Anello in oro bianco 18k con zaffiro Ceylon e diamanti laterali",
    image: "https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=400&h=300&fit=crop",
    category: "Anelli"
  },
  {
    idea: "Collana con iniziali per la mamma",
    result: "Ciondolo in oro giallo con lettere intrecciate e catena veneziana",
    image: "https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=400&h=300&fit=crop",
    category: "Collane"
  },
  {
    idea: "Bracciale uomo minimalista",
    result: "Bracciale in argento satinato con chiusura magnetica personalizzata",
    image: "https://images.unsplash.com/photo-1611652022419-a9419f74343d?w=400&h=300&fit=crop",
    category: "Bracciali"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "L'anello di fidanzamento era esattamente come lo immaginavo. L'orafo ha interpretato perfettamente i miei desideri.",
    author: "Alessandro M.",
    location: "Milano"
  },
  {
    text: "Ho regalato a mia moglie una collana unica per il nostro anniversario. La qualità artigianale italiana non ha paragoni.",
    author: "Roberto S.",
    location: "Roma"
  },
  {
    text: "Cercavo orecchini particolari e ho trovato un artigiano che ha creato un design esclusivo solo per me.",
    author: "Francesca L.",
    location: "Firenze"
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: "È gratuito inviare una richiesta?",
    answer: "Sì, è completamente gratuito. Descrivi il gioiello dei tuoi sogni e ricevi proposte da orafi e designer senza alcun costo."
  },
  {
    question: "Sono obbligato ad acquistare?",
    answer: "Assolutamente no. Ricevi le proposte, le valuti con calma e decidi solo se trovi il gioiello perfetto per te."
  },
  {
    question: "Gli artigiani sono orafi certificati?",
    answer: "Sì, su Rarifex trovi orafi, gioiellieri e designer con anni di esperienza, tutti verificati e specializzati in creazioni Made in Italy."
  },
  {
    question: "Posso portare le mie pietre o l'oro?",
    answer: "Certamente! Molti artigiani accettano di lavorare con materiali forniti dal cliente. Specifica questa esigenza nella descrizione."
  },
  {
    question: "Come funziona per le misure?",
    answer: "L'artigiano ti guiderà nella presa delle misure. Per gli anelli, spesso viene inviato un misuratore gratuito a casa."
  }
];

export default function GioielliRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    jewelry_type: '',
    material: '',
    occasion: '',
    recipient: '',
    budget: '',
    timeline: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    document.title = 'Gioielli su Misura | Orafi Italiani | Rarifex';
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.attachments.length + files.length > 5) {
      toast.error('Puoi caricare massimo 5 immagini');
      return;
    }
    
    setUploadingFile(true);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error('Solo immagini sono permesse');
        continue;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Immagine troppo grande (max 5MB)');
        continue;
      }
      
      try {
        const fileName = `jewelry-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const filePath = `requests/${fileName}`;
        
        const { error } = await supabase.storage
          .from('rarifex-uploads')
          .upload(filePath, file);
        
        if (error) throw error;
        
        const { data: { publicUrl } } = supabase.storage
          .from('rarifex-uploads')
          .getPublicUrl(filePath);
        
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, publicUrl]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Errore nel caricamento');
      }
    }
    
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Descrivi il gioiello che desideri');
      return;
    }
    
    if (!user) {
      setShowAuthPrompt(true);
      localStorage.setItem('rarifex_pending_jewelry_request', JSON.stringify(formData));
      return;
    }
    
    setLoading(true);
    
    try {
      const jewelryTypeLabel = JEWELRY_TYPES.find(t => t.value === formData.jewelry_type)?.label || '';
      const materialLabel = MATERIALS.find(m => m.value === formData.material)?.label || '';
      const occasionLabel = JEWELRY_OCCASIONS.find(o => o.value === formData.occasion)?.label || '';
      const recipientLabel = JEWELRY_RECIPIENTS.find(r => r.value === formData.recipient)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      // Build formatted description with all form data
      let fullDescription = formData.description;
      
      const details = [];
      if (jewelryTypeLabel) details.push(`💎  Tipo gioiello: ${jewelryTypeLabel}`);
      if (materialLabel) details.push(`✨  Materiale: ${materialLabel}`);
      if (occasionLabel) details.push(`🎁  Occasione: ${occasionLabel}`);
      if (recipientLabel) details.push(`👤  Per chi: ${recipientLabel}`);
      if (budgetLabel) details.push(`💰  Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️  Tempi: ${timelineLabel}`);
      
      if (details.length > 0) {
        const separator = '━━━━━━━━━━━━━━━━━━━━━━━';
        fullDescription += `\n\n${separator}\n✨ DETTAGLI RICHIESTA\n${separator}\n` + details.join('\n');
      }
      
      const requestData = {
        client_id: user.id,
        title: formData.title || `${jewelryTypeLabel} in ${materialLabel}`.trim() || 'Gioiello su misura',
        description: fullDescription,
        category: 'Gioielli',
        price_range: formData.budget || 'flexible',
        timeline: formData.timeline || 'flexible',
        attachments: formData.attachments,
      };
      
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la creazione');
      }
      
      toast.success('Richiesta inviata! Riceverai presto proposte dagli orafi.');
      navigate('/requests');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('jewelry-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Gioielli su Misura | Orafi e Designer Italiani | Rarifex</title>
        <meta name="description" content="Crea il gioiello dei tuoi sogni con orafi italiani. Anelli, collane, bracciali personalizzati. Descrivi la tua idea e ricevi proposte da maestri orafi. Gratuito e senza impegno." />
        <meta name="keywords" content="gioielli su misura, anello personalizzato, collana artigianale, orafo italiano, gioielli made in italy, anello fidanzamento, fedi nuziali artigianali" />
        <link rel="canonical" href="https://www.rarifex.com/gioielli-su-misura" />
        <meta property="og:title" content="Gioielli su Misura | Orafi Italiani | Rarifex" />
        <meta property="og:description" content="Crea il gioiello dei tuoi sogni con orafi italiani. Gratuito e senza impegno." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/gioielli-su-misura" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-teal-50/50 via-white to-amber-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-teal-100/50 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                
                {/* Left: Hero Content */}
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Gem className="w-4 h-4" />
                    Gioielli su Misura
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    Il gioiello{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-amber-500">
                      dei tuoi sogni
                    </span>
                    {' '}esiste
                  </h1>
                  
                  <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed">
                    Maestri orafi italiani lo creano apposta per te.
                  </p>
                  
                  <p className="text-lg text-slate-500 mb-8">
                    Descrivi il gioiello che immagini e ricevi proposte personalizzate da orafi e designer italiani.
                    <br />
                    <strong className="text-slate-700">Gratuito e senza impegno.</strong>
                  </p>
                  
                  {/* Trust badges */}
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>Orafi certificati</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-teal-500" />
                      <span>Risposte in 24-48h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-blue-500" />
                      <span>100% Made in Italy</span>
                    </div>
                  </div>
                </div>
                
                {/* Right: Form Card */}
                <div id="jewelry-form" className="relative">
                  <Card className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      
                      {/* Title */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Titolo del tuo gioiello
                        </Label>
                        <Input
                          placeholder="Es: Anello di fidanzamento con smeraldo"
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          className="mt-1.5"
                          data-testid="jewelry-title-input"
                        />
                      </div>
                      
                      {/* Description */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Descrivi il gioiello che desideri *
                        </Label>
                        <Textarea
                          placeholder="Racconta la tua idea... Stile, pietre, incisioni, dettagli particolari. Più descrivi, meglio gli orafi capiranno la tua visione."
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          className="mt-1.5 min-h-[120px]"
                          required
                          data-testid="jewelry-description-textarea"
                        />
                      </div>
                      
                      {/* Jewelry Type & Material */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">
                            Tipo di gioiello
                          </Label>
                          <select
                            value={formData.jewelry_type}
                            onChange={(e) => handleInputChange('jewelry_type', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                            data-testid="jewelry-type-select"
                          >
                            <option value="">Seleziona...</option>
                            {JEWELRY_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">
                            Materiale preferito
                          </Label>
                          <select
                            value={formData.material}
                            onChange={(e) => handleInputChange('material', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                            data-testid="jewelry-material-select"
                          >
                            <option value="">Seleziona...</option>
                            {MATERIALS.map(mat => (
                              <option key={mat.value} value={mat.value}>{mat.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Occasion & Recipient */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Per quale occasione?</Label>
                          <select
                            value={formData.occasion}
                            onChange={(e) => handleInputChange('occasion', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                            data-testid="jewelry-occasion-select"
                          >
                            <option value="">Seleziona...</option>
                            {JEWELRY_OCCASIONS.map(occ => (
                              <option key={occ.value} value={occ.value}>{occ.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Per chi è?</Label>
                          <select
                            value={formData.recipient}
                            onChange={(e) => handleInputChange('recipient', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                            data-testid="jewelry-recipient-select"
                          >
                            <option value="">Seleziona...</option>
                            {JEWELRY_RECIPIENTS.map(rec => (
                              <option key={rec.value} value={rec.value}>{rec.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Budget & Timeline */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Budget</Label>
                          <select
                            value={formData.budget}
                            onChange={(e) => handleInputChange('budget', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                            data-testid="jewelry-budget-select"
                          >
                            <option value="">Seleziona...</option>
                            {BUDGET_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label} - {opt.description}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Tempi</Label>
                          <select
                            value={formData.timeline}
                            onChange={(e) => handleInputChange('timeline', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                            data-testid="jewelry-timeline-select"
                          >
                            <option value="">Seleziona...</option>
                            {TIMELINE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label} - {opt.description}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Attachments */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Immagini di ispirazione (consigliate!)
                        </Label>
                        <div className="mt-1.5">
                          {formData.attachments.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {formData.attachments.map((url, idx) => (
                                <div key={idx} className="relative group">
                                  <img src={url} alt="" className="w-16 h-16 object-cover rounded-lg" />
                                  <button
                                    type="button"
                                    onClick={() => removeAttachment(idx)}
                                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {formData.attachments.length < 5 && (
                            <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-teal-300 hover:bg-teal-50/50 transition-colors">
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleFileUpload}
                                className="hidden"
                                disabled={uploadingFile}
                                data-testid="jewelry-file-upload"
                              />
                              {uploadingFile ? (
                                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                              ) : (
                                <Upload className="w-5 h-5 text-slate-400" />
                              )}
                              <span className="text-sm text-slate-500">
                                Carica foto di riferimento ({formData.attachments.length}/5)
                              </span>
                            </label>
                          )}
                        </div>
                      </div>
                      
                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-teal-500 to-amber-500 hover:from-teal-600 hover:to-amber-600 text-white shadow-lg shadow-teal-200"
                        data-testid="jewelry-submit-button"
                      >
                        {loading ? (
                          <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        ) : (
                          <Gem className="w-5 h-5 mr-2" />
                        )}
                        Ricevi proposte gratis
                      </Button>
                      
                    </form>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2: PROBLEMA */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8">
                Il gioiello perfetto non si trova in vetrina
              </h2>
              
              <div className="space-y-4 text-lg text-slate-600 mb-8">
                <p className="flex items-center justify-center gap-3">
                  <span className="text-teal-400">✗</span>
                  I gioielli in serie sono tutti uguali
                </p>
                <p className="flex items-center justify-center gap-3">
                  <span className="text-teal-400">✗</span>
                  Non trovi quello che hai in mente
                </p>
                <p className="flex items-center justify-center gap-3">
                  <span className="text-teal-400">✗</span>
                  Vuoi qualcosa di veramente unico e personale
                </p>
              </div>
              
              <p className="text-xl text-slate-700 font-medium">
                Per un gioiello speciale, serve un orafo che lo crei per te.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 3: SOLUZIONE */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Un gioiello{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-amber-500">
                  unico come te
                </span>
              </h2>
              
              <p className="text-xl text-slate-600 mb-12">
                Orafi e designer italiani pronti a trasformare la tua idea in realtà.
                <br />
                <strong className="text-slate-800">Tu immagini, loro creano.</strong>
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-teal-100 flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-8 h-8 text-teal-500" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Descrivi la tua idea</h3>
                  <p className="text-slate-600">Racconta il gioiello che immagini, anche a grandi linee</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-amber-500" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Orafi ti propongono</h3>
                  <p className="text-slate-600">Ricevi bozzetti e preventivi da maestri orafi</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Scegli il migliore</h3>
                  <p className="text-slate-600">Nessun obbligo, decidi solo se ti convince</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 4: ESEMPI */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-4">
                Da idea a gioiello
              </h2>
              <p className="text-xl text-slate-600 text-center mb-12">
                Ecco cosa hanno creato i nostri orafi
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                {JEWELRY_EXAMPLES.map((example, idx) => (
                  <Card key={idx} className="overflow-hidden hover:shadow-xl transition-shadow">
                    <img 
                      src={example.image} 
                      alt={example.result}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-5">
                      <span className="text-xs font-medium text-teal-500 uppercase tracking-wider">
                        {example.category}
                      </span>
                      <p className="text-sm text-slate-500 mt-2 mb-1">
                        Richiesta: "{example.idea}"
                      </p>
                      <p className="font-semibold text-slate-800">
                        → {example.result}
                      </p>
                    </div>
                  </Card>
                ))}
              </div>
              
              <div className="text-center mt-10">
                <Button 
                  onClick={scrollToForm}
                  variant="outline"
                  className="border-teal-200 text-teal-600 hover:bg-teal-50"
                >
                  Vuoi qualcosa di simile? Descrivi la tua idea
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 5: COME FUNZIONA */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-teal-50/50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                In 3 semplici passaggi
              </h2>
              
              <div className="space-y-8">
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-teal-500 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Descrivi il tuo gioiello</h3>
                    <p className="text-slate-600">
                      Racconta cosa hai in mente: tipo di gioiello, materiali, pietre, stile. Allega immagini di ispirazione se le hai.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Ricevi proposte</h3>
                    <p className="text-slate-600">
                      Orafi e designer vedono la tua richiesta e ti inviano bozzetti, rendering 3D e preventivi dettagliati.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Scegli l'orafo perfetto</h3>
                    <p className="text-slate-600">
                      Confronta le proposte, chatta con gli orafi per affinare i dettagli. Nessun obbligo, scegli solo se sei soddisfatto.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 6: PERCHÉ FUNZIONA */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Perché scegliere un gioiello artigianale
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-teal-50">
                  <CheckCircle className="w-8 h-8 text-teal-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Pezzo unico</h3>
                    <p className="text-sm text-slate-600">Nessuno avrà mai lo stesso gioiello</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-amber-50">
                  <Sparkles className="w-8 h-8 text-amber-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Personalizzazione totale</h3>
                    <p className="text-sm text-slate-600">Ogni dettaglio come lo desideri tu</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-blue-50">
                  <Shield className="w-8 h-8 text-blue-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Qualità certificata</h3>
                    <p className="text-sm text-slate-600">Materiali pregiati e lavorazione italiana</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-green-50">
                  <Zap className="w-8 h-8 text-green-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Zero rischio</h3>
                    <p className="text-sm text-slate-600">Nessun obbligo di acquisto</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 7: TESTIMONIALS */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Cosa dicono i clienti
              </h2>
              
              <div className="grid md:grid-cols-3 gap-6">
                {TESTIMONIALS.map((testimonial, idx) => (
                  <Card key={idx} className="p-6 bg-white">
                    <div className="flex gap-1 mb-4">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} className="w-5 h-5 fill-teal-400 text-teal-400" />
                      ))}
                    </div>
                    <p className="text-slate-600 mb-4 italic">
                      "{testimonial.text}"
                    </p>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center font-semibold text-teal-600">
                        {testimonial.author[0]}
                      </div>
                      <div>
                        <p className="font-medium">{testimonial.author}</p>
                        <p className="text-slate-500 text-xs">{testimonial.location}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 8: FAQ */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Domande frequenti
              </h2>
              
              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, idx) => (
                  <div 
                    key={idx}
                    className="border border-slate-200 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                      className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
                      data-testid={`jewelry-faq-${idx}`}
                    >
                      <span className="font-medium text-slate-800">{faq.question}</span>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedFaq === idx ? 'rotate-90' : ''}`} />
                    </button>
                    {expandedFaq === idx && (
                      <div className="px-5 pb-5 text-slate-600">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 9: CTA FINALE */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-teal-50 to-amber-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <Gem className="w-16 h-16 text-teal-500 mx-auto mb-6" />
              
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Pronto a creare il tuo gioiello?
              </h2>
              
              <p className="text-xl text-slate-600 mb-8">
                I maestri orafi italiani aspettano la tua idea.
              </p>
              
              <Button
                onClick={scrollToForm}
                size="lg"
                className="py-6 px-10 text-lg font-semibold bg-gradient-to-r from-teal-500 to-amber-500 hover:from-teal-600 hover:to-amber-600 text-white shadow-lg shadow-teal-200"
                data-testid="jewelry-cta-final-button"
              >
                <Gem className="w-5 h-5 mr-2" />
                Ricevi proposte gratis ora
              </Button>
            </div>
          </div>
        </section>

      </div>

      {/* Auth Prompt Modal */}
      {showAuthPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Accedi per continuare</h3>
            <p className="text-slate-600 mb-6">
              Per inviare la tua richiesta e ricevere proposte dagli orafi, devi prima accedere o creare un account.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowAuthPrompt(false)}
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                onClick={() => navigate('/login?redirect=/gioielli-su-misura')}
                className="flex-1 bg-teal-500 hover:bg-teal-600"
              >
                Accedi
              </Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
